import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { usePlanning } from '../context/PlanningContext';
import ResourceList from '../components/ResourceList';
import ProjectList from '../components/ProjectList';
import MonthGrid from '../components/MonthGrid';
import type { Progetto } from '../types';
import { GruppoProgetti } from '../types/gruppo';

const PlanningBoard: React.FC = () => {
  const {
    anno,
    setAnno,
    selectedRisorsa,
    loading,
    error,
    saveAllocazione,
  } = usePlanning();

  const [draggedProject, setDraggedProject] = useState<Progetto | null>(null);
  const [draggedGruppo, setDraggedGruppo] = useState<GruppoProgetti | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    const progetto = event.active.data.current?.progetto;
    const gruppo = event.active.data.current?.gruppo;

    if (gruppo) {
      setDraggedGruppo(gruppo);
      setDraggedProject(null);
    } else if (progetto) {
      setDraggedProject(progetto);
      setDraggedGruppo(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedProject(null);
    setDraggedGruppo(null);

    if (!over) return;

    const progetto = active.data.current?.progetto;
    const gruppo = active.data.current?.gruppo;
    const dropData = over.data.current;

    if (!dropData || !dropData.risorsa || !dropData.mese || !selectedRisorsa) return;

    // Caso 1: Drop di un singolo progetto
    if (progetto) {
      try {
        await saveAllocazione({
          id_risorsa: selectedRisorsa.id,
          id_progetto: progetto.id,
          anno,
          mese: dropData.mese,
          percentuale: 0,
        });
      } catch (err: any) {
        alert(err.message || 'Errore nel salvataggio');
      }
    }

    // Caso 2: Drop di un gruppo (batch insert)
    if (gruppo && gruppo.progetti && gruppo.progetti.length > 0) {
      try {
        let successCount = 0;
        let skippedCount = 0;
        const errors: string[] = [];

        for (const prog of gruppo.progetti) {
          try {
            await saveAllocazione({
              id_risorsa: selectedRisorsa.id,
              id_progetto: prog.id,
              anno,
              mese: dropData.mese,
              percentuale: 0,
            });
            successCount++;
          } catch (err: any) {
            // Se il progetto è già allocato, viene skippato silenziosamente
            if (err.message && err.message.includes('già allocato')) {
              skippedCount++;
            } else {
              errors.push(`${prog.id_hrp || prog.titolo}: ${err.message}`);
            }
          }
        }

        // Messaggio riassuntivo
        let message = `Gruppo "${gruppo.nome}": ${successCount} progetti allocati`;
        if (skippedCount > 0) {
          message += `, ${skippedCount} già presenti`;
        }
        if (errors.length > 0) {
          message += `\n\nErrori:\n${errors.join('\n')}`;
          alert(message);
        } else if (successCount > 0 || skippedCount > 0) {
          // Mostra notifica di successo (opzionale)
          console.log(message);
        }
      } catch (err: any) {
        alert(err.message || 'Errore nel salvataggio del gruppo');
      }
    }
  };

  const handleDragCancel = () => {
    setDraggedProject(null);
    setDraggedGruppo(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Container maxWidth={false} sx={{ py: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                HarpaPlanner - Pianificazione Risorse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Seleziona una risorsa e trascina i progetti nei mesi
              </Typography>
            </Box>
            <TextField
              label="Anno"
              type="number"
              value={anno}
              onChange={(e) => setAnno(parseInt(e.target.value))}
              sx={{ width: 120 }}
              size="small"
            />
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Layout 3 colonne */}
        <Grid container spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
          {/* Colonna 1: Risorse */}
          <Grid item xs={12} md={2} sx={{ height: '100%', overflow: 'auto' }}>
            <ResourceList />
          </Grid>

          {/* Colonna 2: Progetti */}
          <Grid item xs={12} md={2} sx={{ height: '100%', overflow: 'auto' }}>
            <ProjectList />
          </Grid>

          {/* Colonna 3: Griglia Mesi */}
          <Grid item xs={12} md={8} sx={{ height: '100%', overflow: 'auto' }}>
            {selectedRisorsa ? (
              <MonthGrid risorsa={selectedRisorsa} />
            ) : (
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Seleziona una risorsa per iniziare
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clicca su una risorsa dalla lista a sinistra
                  </Typography>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>

      </Container>

      <DragOverlay>
        {draggedProject && (
          <Paper
            elevation={6}
            sx={{
              p: 2,
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              cursor: 'grabbing',
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              {draggedProject.titolo}
            </Typography>
            {draggedProject.id_hrp && (
              <Typography variant="caption">
                ID HRP: {draggedProject.id_hrp}
              </Typography>
            )}
          </Paper>
        )}
        {draggedGruppo && (
          <Paper
            elevation={6}
            sx={{
              p: 2,
              bgcolor: draggedGruppo.colore || 'primary.main',
              color: '#fff',
              cursor: 'grabbing',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6">{draggedGruppo.nome}</Typography>
            <Typography variant="caption">
              {draggedGruppo.progetti.length} progetti
            </Typography>
          </Paper>
        )}
      </DragOverlay>
    </DndContext>
  );
};

const PianificazionePage: React.FC = () => {
  return <PlanningBoard />;
};

export default PianificazionePage;
